import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Phone, Mail, ShieldCheck, Clock, Users, Wrench, ArrowRight, CheckCircle2 } from 'lucide-react'
import Lightbox from '../components/Lightbox'

// Lokale fallbacks, der matcher de seeded data præcist, hvis netværket er nede
const FALLBACK_MASKINER = [
  {
    id: "1",
    title: { da: "Caterpillar 301.8 Minigraver" },
    category: { da: "Minigravere" },
    price: { da: "Fra 1.200 kr./dag ekskl. moms" },
    images: ["https://assets.auxodev.dk/assets/4eef1727-4c51-4dbe-8773-4e974f33a84d/be81d1e6-fc9f-4ee9-86fc-8ad27ff0c3f3.jpg"],
    slug: { da: "caterpillar-301-8-minigraver" },
    description: { da: "Vores Caterpillar 301.8 minigraver er den perfekte maskine til mindre og mellemstore graveopgaver. Den is kompakt, stærk og nem at manøvrere." }
  },
  {
    id: "2",
    title: { da: "Husqvarna Pladevibrator & Jordloppe" },
    category: { da: "Komprimeringsmateriel" },
    price: { da: "Fra 250 kr./dag ekskl. moms" },
    images: ["https://assets.auxodev.dk/assets/4eef1727-4c51-4dbe-8773-4e974f33a84d/a7427b13-3098-4c44-892e-8dfc3c9780ba.jpg"],
    slug: { da: "husqvarna-pladevibrator-jordloppe" },
    description: { da: "Effektive maskiner til komprimering af sand, grus og jord. Perfekt til forberedelse af flisebelægning, indkørsler og terrasser." }
  },
  {
    id: "3",
    title: { da: "SoRoTo 120L Tvangsblander" },
    category: { da: "Blandemaskiner" },
    price: { da: "Fra 300 kr./dag ekskl. moms" },
    images: ["https://assets.auxodev.dk/assets/4eef1727-4c51-4dbe-8773-4e974f33a84d/a4dfb078-af8c-4ef7-9de8-217f19db1774.jpg"],
    slug: { da: "soroto-120l-tvangsblander" },
    description: { da: "Den originale SoRoTo 120L tvangsblander blander hurtigt og effektivt alt fra beton og mørtel til puds og epoxy." }
  }
]

const PREVIEW_IMAGES = [
  "https://assets.auxodev.dk/assets/4eef1727-4c51-4dbe-8773-4e974f33a84d/7e0fc6fa-1cd3-4cb9-a724-be2249d92538.jpg",
  "https://assets.auxodev.dk/assets/4eef1727-4c51-4dbe-8773-4e974f33a84d/91edf261-1889-4fa4-9347-dc80719d697f.jpg",
  "https://assets.auxodev.dk/assets/4eef1727-4c51-4dbe-8773-4e974f33a84d/63a1fca8-5db7-4d6f-bb78-db9bdae5f318.jpg",
  "https://assets.auxodev.dk/assets/4eef1727-4c51-4dbe-8773-4e974f33a84d/356bf3d5-c739-41d4-86bd-f9993507a915.jpg"
]

const LOGO_URL = "https://assets.auxodev.dk/assets/4eef1727-4c51-4dbe-8773-4e974f33a84d/6fc95741-bbf3-49e6-9c69-766b0204a25f.png"

export default function Forside() {
  const [maskiner, setMaskiner] = useState([])
  const [loading, setLoading] = useState(true)
  const [lightboxImages, setLightboxImages] = useState([])
  const [lightboxIndex, setLightboxIndex] = useState(null)

  const openLightbox = (images, index) => {
    setLightboxImages(images)
    setLightboxIndex(index)
  }

  useEffect(() => {
    // Sæt meta-data
    document.title = "D&W Udlejning ApS | Udlejning af mindre entreprenørmaskiner i Skibby"
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) {
      metaDesc.setAttribute('content', 'D&W Udlejning ApS tilbyder udlejning af mindre entreprenørmaskiner til både private og erhverv. Fleksibel maskinudlejning i Skibby og omegn med fokus på kvalitet og god service.')
    }

    fetch('https://api.dev.auxo.dk/public/content/maskiner')
      .then((res) => {
        if (!res.ok) throw new Error('Kunne ikke hente maskiner')
        return res.json()
      })
      .then((data) => {
        if (data && data.length > 0) {
          setMaskiner(data)
        } else {
          setMaskiner(FALLBACK_MASKINER)
        }
        setLoading(false)
      })
      .catch((err) => {
        console.error('Fejl ved hentning af maskiner fra CMS:', err)
        setMaskiner(FALLBACK_MASKINER)
        setLoading(false)
      })
  }, [])

  return (
    <div className="bg-neutral-50 text-neutral-900 min-h-screen selection:bg-amber-500 selection:text-neutral-950">
      {/* 1. HERO SEKTION */}
      <section className="relative h-[85vh] flex items-center justify-center overflow-hidden">
        {/* Baggrundsbillede - Caterpillar på trailer */}
        <button
          onClick={() => openLightbox(["https://assets.auxodev.dk/assets/4eef1727-4c51-4dbe-8773-4e974f33a84d/884fb8bf-26fc-45ca-b106-2975e775d2bf.jpg"], 0)}
          className="absolute inset-0 z-0 w-full h-full text-left focus:outline-none cursor-zoom-in group"
          aria-label="Forstør baggrundsbillede"
        >
          <img
            src="https://assets.auxodev.dk/assets/4eef1727-4c51-4dbe-8773-4e974f33a84d/884fb8bf-26fc-45ca-b106-2975e775d2bf.jpg"
            alt="Caterpillar minigraver på trailer - D&W Udlejning ApS"
            className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-[1.02]"
          />
          {/* Mørkt overlay for optimal læsbarhed, der fader til den lyse baggrund i bunden */}
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-50 via-neutral-950/80 to-neutral-900/60 transition-opacity duration-300 group-hover:opacity-90" />
        </button>

        {/* Hero Indhold */}
        <div className="relative z-10 max-w-5xl mx-auto px-6 sm:px-8 lg:px-8 text-center">
          <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[10px] sm:text-xs font-bold bg-amber-500/20 text-amber-400 border border-amber-500/30 uppercase tracking-widest mb-6">
            <ShieldCheck className="w-4 h-4" /> Professionel Maskinudlejning i Skibby
          </span>
          <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-white leading-tight">
            Udlejning af mindre <br className="hidden sm:inline" />
            <span className="text-amber-500">entreprenørmaskiner</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-neutral-200 max-w-2xl mx-auto font-medium leading-relaxed">
            D&W Udlejning ApS tilbyder fleksibel og pålidelig maskinudlejning til både private og erhverv. Vi leverer driftssikre maskiner, der sikrer, at dit projekt kommer sikkert i mål.
          </p>

          {/* CTA Knapper */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:20828970"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-5 py-3 sm:px-8 sm:py-4 rounded-md bg-amber-500 hover:bg-amber-400 text-neutral-950 font-bold text-sm sm:text-base transition-all duration-200 shadow-lg shadow-amber-500/20 whitespace-nowrap"
            >
              <Phone className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
              <span>Ring nu: 20 82 89 70</span>
            </a>
            <a
              href="mailto:dw@dw-udlejning.dk"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-5 py-3 sm:px-8 sm:py-4 rounded-md bg-neutral-900/90 hover:bg-neutral-900 border border-neutral-700 text-white font-bold text-sm sm:text-base transition-all duration-200 whitespace-nowrap"
            >
              <Mail className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
              <span>Send mail: dw@dw-udlejning.dk</span>
            </a>
          </div>
        </div>
      </section>

      {/* 2. INTRODUKTION TIL VIRKSOMHEDEN */}
      <section className="relative py-24 bg-white overflow-hidden">
        {/* Subtilt geometrisk grid-mønster og logo-vandmærke */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#f3f4f6_1px,transparent_1px),linear-gradient(to_bottom,#f3f4f6_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none opacity-60" />
        <div className="absolute right-[-10%] bottom-[-10%] w-[500px] h-[500px] pointer-events-none opacity-[0.03] select-none">
          <img src={LOGO_URL} alt="D&W Logo Vandmærke" className="w-full h-full object-contain" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <span className="inline-block text-xs font-bold text-amber-600 uppercase tracking-widest bg-amber-50 px-3 py-1 rounded-md border border-amber-200">
                Hvem er vi?
              </span>
              <h2 className="text-3xl sm:text-4xl font-black text-neutral-900 tracking-tight">
                Din lokale maskinudlejning i Skibby og omegn
              </h2>
              <p className="text-neutral-600 text-base sm:text-lg leading-relaxed">
                Hos D&W Udlejning ApS har vi gjort det nemt, hurtigt og overskueligt at leje entreprenørmaskiner til dit næste projekt. Uanset om du er privat husejer, der skal anlægge ny have eller indkørsel, eller en professionel erhvervsdrivende, der mangler ekstra kapacitet på byggepladsen, står vi klar til at hjælpe dig.
              </p>
              <p className="text-neutral-500 text-sm sm:text-base leading-relaxed">
                Vi lægger stor vægt på personlig service, høj fleksibilitet og absolut driftssikkerhed. Alle vores maskiner er af nyere dato, topvedligeholdte og grundigt rengjorte inden udlejning, så du kan fokusere 100% på dit arbejde uden uforudsete stop.
              </p>
              <div className="pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-2.5 text-sm font-bold text-neutral-700">
                  <CheckCircle2 className="w-5 h-5 text-amber-500 shrink-0" />
                  <span>Udlejning til private & erhverv</span>
                </div>
                <div className="flex items-center gap-2.5 text-sm font-bold text-neutral-700">
                  <CheckCircle2 className="w-5 h-5 text-amber-500 shrink-0" />
                  <span>Fleksible lejeperioder</span>
                </div>
                <div className="flex items-center gap-2.5 text-sm font-bold text-neutral-700">
                  <CheckCircle2 className="w-5 h-5 text-amber-500 shrink-0" />
                  <span>Driftssikkert materiel</span>
                </div>
                <div className="flex items-center gap-2.5 text-sm font-bold text-neutral-700">
                  <CheckCircle2 className="w-5 h-5 text-amber-500 shrink-0" />
                  <span>Konkurrencedygtige priser</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-5">
              <button
                onClick={() => openLightbox(["https://assets.auxodev.dk/assets/4eef1727-4c51-4dbe-8773-4e974f33a84d/3d5437db-dba8-4d8c-975c-d94fa900b443.jpg"], 0)}
                className="relative rounded-xl overflow-hidden border border-neutral-200 shadow-xl bg-neutral-100 w-full text-left focus:outline-none cursor-zoom-in group"
                aria-label="Forstør introduktionsbillede"
              >
                <img
                  src="https://assets.auxodev.dk/assets/4eef1727-4c51-4dbe-8773-4e974f33a84d/3d5437db-dba8-4d8c-975c-d94fa900b443.jpg"
                  alt="Minigraver i arbejde i have - D&W Udlejning ApS"
                  className="w-full h-96 object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-neutral-950/60 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 bg-white/95 border border-neutral-200 p-4 rounded-lg backdrop-blur-sm shadow-lg">
                  <p className="text-xs font-bold text-amber-600 uppercase tracking-widest">Lokation</p>
                  <p className="text-sm font-bold text-neutral-800 mt-1">Vi dækker hele Skibby, Hornsherred og omegn med hurtig levering.</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 3. FREMHÆVEDE MASKINER */}
      <section className="relative py-24 bg-neutral-100/50 border-t border-b border-neutral-200/80 overflow-hidden">
        {/* Subtilt geometrisk grid-mønster */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#e5e7eb_1px,transparent_1px),linear-gradient(to_bottom,#e5e7eb_1px,transparent_1px)] bg-[size:3rem_3rem] pointer-events-none opacity-40" />
        <div className="absolute left-[-10%] top-[-10%] w-[500px] h-[500px] pointer-events-none opacity-[0.02] select-none">
          <img src={LOGO_URL} alt="D&W Logo Vandmærke" className="w-full h-full object-contain" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <span className="inline-block text-xs font-bold text-amber-600 uppercase tracking-widest bg-amber-50 px-3 py-1 rounded-md border border-amber-200">
              Vores maskiner
            </span>
            <h3 className="text-3xl sm:text-4xl font-black text-neutral-900 tracking-tight">
              Professionelle maskiner klar til udlejning
            </h3>
            <p className="text-neutral-600 text-base">
              Vi tilbyder et fokuseret udvalg af stærke og driftssikre maskiner. Se vores udvalg herunder og kontakt os for at booke.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {maskiner.map((maskine) => {
                const isSoroto = maskine.slug?.da === 'soroto-120l-tvangsblander' || maskine.title?.da?.includes('SoRoTo');
                return (
                  <div
                    key={maskine.id}
                    className={`bg-white border border-neutral-200 rounded-xl overflow-hidden flex flex-col shadow-sm hover:shadow-md transition-all duration-200 ${
                      isSoroto ? 'md:col-span-2 lg:col-span-1' : ''
                    }`}
                  >
                    {/* Billede */}
                    <button
                      onClick={() => openLightbox(maskine.images || ['https://assets.auxodev.dk/assets/4eef1727-4c51-4dbe-8773-4e974f33a84d/be81d1e6-fc9f-4ee9-86fc-8ad27ff0c3f3.jpg'], 0)}
                      className="relative bg-neutral-100 overflow-hidden w-full text-left focus:outline-none cursor-zoom-in group h-48 sm:h-64"
                      aria-label={`Forstør billede af ${maskine.title?.da}`}
                    >
                      <img
                        src={maskine.images?.[0] || 'https://assets.auxodev.dk/assets/4eef1727-4c51-4dbe-8773-4e974f33a84d/be81d1e6-fc9f-4ee9-86fc-8ad27ff0c3f3.jpg'}
                        alt={maskine.title?.da}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                      <span className="absolute top-3 left-3 bg-white/95 border border-neutral-200 text-amber-600 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md shadow-sm">
                        {maskine.category?.da}
                      </span>
                      <div className="absolute inset-0 bg-neutral-900/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                        <span className="px-3 py-1.5 rounded-md bg-white/95 border border-neutral-200 text-[10px] font-bold text-amber-600 uppercase tracking-wider shadow-sm">
                          Forstør
                        </span>
                      </div>
                    </button>

                    {/* Indhold */}
                    <div className="p-5 flex-grow flex flex-col justify-between">
                      <div className="space-y-2">
                        <h4 className="text-lg font-bold text-neutral-900 tracking-tight line-clamp-1">
                          {maskine.title?.da}
                        </h4>
                        <p className="text-neutral-500 text-xs leading-relaxed line-clamp-3">
                          {maskine.description?.da?.replace(/<[^>]*>/g, '') || ''}
                        </p>
                      </div>

                      <div className="mt-5 pt-4 border-t border-neutral-100 flex items-center justify-between">
                        <div>
                          <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest">Pris pr. dag</p>
                          <p className="text-sm font-black text-amber-600 mt-0.5">{maskine.price?.da}</p>
                        </div>
                        <Link
                          to={`/udlejning#${maskine.slug?.da}`}
                          className="p-2 rounded-md bg-neutral-100 hover:bg-amber-500 hover:text-neutral-950 text-neutral-600 transition-all duration-200"
                          aria-label={`Læs mere om ${maskine.title?.da}`}
                        >
                          <ArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-12 text-center">
            <Link
              to="/udlejning"
              className="inline-flex items-center gap-2 text-sm font-bold text-amber-600 hover:text-amber-700 transition-colors duration-200"
            >
              <span>Se alle maskiner og specifikationer</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* 4. FORDELE VED AT VÆLGE OS */}
      <section className="relative py-24 bg-white overflow-hidden">
        {/* Subtilt geometrisk grid-mønster og logo-vandmærke */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#f3f4f6_1px,transparent_1px),linear-gradient(to_bottom,#f3f4f6_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none opacity-60" />
        <div className="absolute right-[-10%] top-[-10%] w-[500px] h-[500px] pointer-events-none opacity-[0.03] select-none">
          <img src={LOGO_URL} alt="D&W Logo Vandmærke" className="w-full h-full object-contain" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <span className="inline-block text-xs font-bold text-amber-600 uppercase tracking-widest bg-amber-50 px-3 py-1 rounded-md border border-amber-200">
              Dine fordele
            </span>
            <h3 className="text-3xl sm:text-4xl font-black text-neutral-900 tracking-tight">
              Hvorfor vælge D&W Udlejning ApS?
            </h3>
            <p className="text-neutral-600 text-base">
              Vi stræber efter at levere den bedste oplevelse hver gang, du lejer hos os.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Fordel 1 */}
            <div className="bg-white border border-neutral-200 p-6 rounded-xl space-y-4 shadow-sm">
              <div className="w-12 h-12 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <h4 className="text-lg font-bold text-neutral-900 tracking-tight">Driftssikre maskiner</h4>
              <p className="text-neutral-500 text-sm leading-relaxed">
                Vores maskiner er af nyere dato og gennemgår altid grundig kontrol, service og rengøring før udlejning.
              </p>
            </div>

            {/* Fordel 2 */}
            <div className="bg-white border border-neutral-200 p-6 rounded-xl space-y-4 shadow-sm">
              <div className="w-12 h-12 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600">
                <Clock className="w-6 h-6" />
              </div>
              <h4 className="text-lg font-bold text-neutral-900 tracking-tight">Høj fleksibilitet</h4>
              <p className="text-neutral-500 text-sm leading-relaxed">
                Lej præcis i den periode du har brug for – uanset om det er en enkelt dag, en hel uge eller længere tid.
              </p>
            </div>

            {/* Fordel 3 */}
            <div className="bg-white border border-neutral-200 p-6 rounded-xl space-y-4 shadow-sm">
              <div className="w-12 h-12 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600">
                <Wrench className="w-6 h-6" />
              </div>
              <h4 className="text-lg font-bold text-neutral-900 tracking-tight">God personlig service</h4>
              <p className="text-neutral-500 text-sm leading-relaxed">
                Vi giver en grundig introduktion til maskinens betjening, så du føler dig helt tryg ved at bruge den.
              </p>
            </div>

            {/* Fordel 4 */}
            <div className="bg-white border border-neutral-200 p-6 rounded-xl space-y-4 shadow-sm">
              <div className="w-12 h-12 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600">
                <Users className="w-6 h-6" />
              </div>
              <h4 className="text-lg font-bold text-neutral-900 tracking-tight">Private & Erhverv</h4>
              <p className="text-neutral-500 text-sm leading-relaxed">
                Vi udlejer til alle. Vi tilpasser lejeaftalen, så den passer perfekt til dit specifikke behov og budget.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. VÆRKTØJSUDLEJNING PREVIEW */}
      <section className="relative py-24 bg-neutral-100/50 border-t border-b border-neutral-200/80 overflow-hidden">
        {/* Subtilt geometrisk grid-mønster */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#e5e7eb_1px,transparent_1px),linear-gradient(to_bottom,#e5e7eb_1px,transparent_1px)] bg-[size:3rem_3rem] pointer-events-none opacity-40" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-start md:items-end justify-between mb-12 gap-4">
            <div>
              <span className="inline-block text-xs font-bold text-amber-600 uppercase tracking-widest bg-amber-50 px-3 py-1 rounded-md border border-amber-200">
                Værktøj
              </span>
              <h3 className="text-3xl sm:text-4xl font-black text-neutral-900 tracking-tight mt-2">
                Professionelt værktøj til leje
              </h3>
            </div>
            <Link
              to="/vaerktojsudlejning"
              className="inline-flex items-center gap-2 text-sm font-bold text-amber-600 hover:text-amber-700 transition-colors duration-200"
            >
              <span>Se alt værktøj</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {PREVIEW_IMAGES.map((url, index) => (
              <button
                key={index}
                onClick={() => openLightbox(PREVIEW_IMAGES, index)}
                className="relative h-48 sm:h-64 rounded-xl overflow-hidden border border-neutral-200 bg-white shadow-sm group text-left focus:outline-none cursor-zoom-in"
                aria-label={`Forstør værktøjsbillede ${index + 1}`}
              >
                <img
                  src={url}
                  alt={`D&W Udlejning værktøj preview ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-neutral-900/10 transition-opacity duration-300 group-hover:opacity-0" />
                <div className="absolute inset-0 bg-neutral-900/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                  <span className="px-3 py-1.5 rounded-md bg-white/95 border border-neutral-200 text-[10px] font-bold text-amber-600 uppercase tracking-wider shadow-sm">
                    Forstør
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* 6. KONTAKTSEKTION MED CTA */}
      <section className="relative py-24 bg-white overflow-hidden">
        {/* Subtilt geometrisk grid-mønster og logo-vandmærke */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#f3f4f6_1px,transparent_1px),linear-gradient(to_bottom,#f3f4f6_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none opacity-60" />
        <div className="absolute left-[5%] bottom-[-10%] w-[400px] h-[400px] pointer-events-none opacity-[0.03] select-none">
          <img src={LOGO_URL} alt="D&W Logo Vandmærke" className="w-full h-full object-contain" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-neutral-900 leading-tight">
            Har du brug for en maskine til dit projekt?
          </h2>
          <p className="text-neutral-600 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            Kontakt os i dag for at høre mere om ledighed, priser eller for at få et uforpligtende tilbud på maskinudlejning i Skibby og omegn. Vi svarer hurtigt på både telefon og mail.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <a
              href="tel:20828970"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 rounded-md bg-amber-500 hover:bg-amber-400 text-neutral-950 font-bold text-base transition-all duration-200 shadow-lg shadow-amber-500/10 whitespace-nowrap"
            >
              <Phone className="w-5 h-5 shrink-0" />
              <span>Ring nu: 20 82 89 70</span>
            </a>
            <Link
              to="/kontakt"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 rounded-md bg-neutral-900 hover:bg-neutral-800 text-white font-bold text-base transition-all duration-200 whitespace-nowrap"
            >
              <Mail className="w-5 h-5 shrink-0" />
              <span>Gå til kontaktformular</span>
            </Link>
          </div>

          <div className="pt-6 text-xs text-neutral-400 font-bold uppercase tracking-widest">
            D&W Udlejning ApS • Nyvej 9 • 4050 Skibby
          </div>
        </div>
      </section>

      {/* Lightbox til stor visning */}
      {lightboxIndex !== null && lightboxImages.length > 0 && (
        <Lightbox
          images={lightboxImages}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNext={() => setLightboxIndex((prev) => (prev + 1) % lightboxImages.length)}
          onPrev={() => setLightboxIndex((prev) => (prev - 1 + lightboxImages.length) % lightboxImages.length)}
        />
      )}
    </div>
  )
}
